import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, Car, Calendar, Mail, Phone, User } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingSuccessPage() {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('loading'); // loading, success, failed
  const [booking, setBooking] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }

    const pollPaymentStatus = async () => {
      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          // Fetch booking details
          if (response.data.metadata?.booking_id) {
            const bookingRes = await axios.get(`${API}/bookings/${response.data.metadata.booking_id}`);
            setBooking(bookingRes.data);
          }
          return;
        }
        
        if (response.data.status === 'expired') {
          setStatus('failed');
          return;
        }
        
        // Continue polling
        if (attempts < 10) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus('failed');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (attempts < 10) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus('failed');
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId, attempts]);

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="booking-success-page">
      <div className="container mx-auto px-4 md:px-8 max-w-2xl">
        <div className="bg-card border border-border rounded-2xl p-8 md:p-12 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="w-16 h-16 mx-auto text-accent animate-spin mb-6" />
              <h1 className="font-serif text-2xl md:text-3xl font-bold mb-4">
                Processing Payment...
              </h1>
              <p className="text-muted-foreground">
                Please wait while we confirm your payment
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-success" />
              </div>
              <h1 className="font-serif text-2xl md:text-3xl font-bold mb-4">
                {t('bookingConfirmed')}
              </h1>
              <p className="text-muted-foreground mb-8">
                {t('paymentSuccess')}
              </p>

              {booking && (
                <div className="bg-secondary rounded-xl p-6 text-left mb-8">
                  <h3 className="font-semibold mb-4">{t('bookingDetails')}</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-3">
                      <User className="w-4 h-4 text-muted-foreground" />
                      <span>{booking.customer_name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <span>{booking.customer_email}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <span>{booking.customer_phone}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <div className="text-sm">
                        <div>Pickup: {new Date(booking.pickup_date).toLocaleDateString()} at {booking.pickup_time || '09:00'}</div>
                        <div>Return: {new Date(booking.return_date).toLocaleDateString()} at {booking.return_time || '09:00'}</div>
                      </div>
                    </div>
                    <div className="border-t border-border pt-3 mt-3 flex justify-between font-semibold">
                      <span>{t('totalPrice')}</span>
                      <span className="text-gold">€{booking.total_price?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/">
                  <Button variant="outline">
                    Back to Home
                  </Button>
                </Link>
                <Link to="/fleet">
                  <Button className="bg-accent hover:bg-accent/90">
                    <Car className="w-4 h-4 mr-2" />
                    Book Another Car
                  </Button>
                </Link>
              </div>
            </>
          )}

          {status === 'failed' && (
            <>
              <div className="w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-12 h-12 text-destructive" />
              </div>
              <h1 className="font-serif text-2xl md:text-3xl font-bold mb-4">
                Payment Failed
              </h1>
              <p className="text-muted-foreground mb-8">
                Something went wrong with your payment. Please try again.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/fleet">
                  <Button className="bg-accent hover:bg-accent/90">
                    Try Again
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline">
                    Contact Support
                  </Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
